import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, ArrowLeft, AlertCircle, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-sm">
        <Link to="/login" className="inline-flex items-center gap-2 text-text-secondary hover:text-white text-sm font-body mb-8 transition-colors" data-testid="back-to-login">
          <ArrowLeft size={16} />
          Back to sign in
        </Link>

        {sent ? (
          <div data-testid="forgot-password-success">
            <div className="w-14 h-14 mb-6 flex items-center justify-center bg-available/10 border border-available/30">
              <Check size={24} className="text-available" />
            </div>
            <h2 className="font-heading text-2xl font-medium tracking-tight text-white mb-2">Check your email</h2>
            <p className="text-text-secondary text-sm font-body mb-6">
              If <span className="text-white font-mono text-xs">{email}</span> is registered, you'll receive a password reset link shortly.
            </p>
            <Link
              to="/login"
              data-testid="return-to-login-button"
              className="inline-block px-5 py-2.5 bg-white text-black text-sm font-body font-medium hover:opacity-90 transition-all"
            >
              Return to sign in
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <h2 className="font-heading text-2xl font-medium tracking-tight text-white" data-testid="forgot-password-heading">
                Reset password
              </h2>
              <p className="text-text-secondary text-sm mt-2 font-body">
                Enter your email and we'll send you a reset link.
              </p>
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 mb-6 border border-booked/30 bg-booked/10 text-sm text-booked" data-testid="forgot-password-error">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-mono text-text-secondary block mb-2">Email</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    data-testid="forgot-password-email-input"
                    required
                    className="w-full bg-surface border border-border text-white pl-10 pr-4 py-2.5 text-sm font-body placeholder:text-text-secondary/50 focus:ring-2 focus:ring-accent focus:border-transparent outline-none transition-all"
                    placeholder="you@company.com"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="forgot-password-submit-button"
                className="w-full bg-white text-black font-body font-medium text-sm py-2.5 flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all duration-200 disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                ) : (
                  'Send reset link'
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
